import JsBarcode from "jsbarcode";
import {
  DataMatrixDefaultPlacement,
  DataMatrixErrorCorrection,
  DataMatrixSymbolInfo,
  DataMatrixWriter,
} from "@zxing/library";
import { GS, normalizeCodeValue } from "./chestnyZnakCore";

const DATA_MATRIX_PAD = 129;
// SymbolShapeHint.FORCE_SQUARE — Честный знак использует только квадратный
// Data Matrix. Без этого короткие коды (01+21 без крипты) дают прямоугольный
// символ (напр. 24×10) — «половинку».
const FORCE_SQUARE = 1;

export const LABEL_INSET_MM = 0.5;

// ─── размеры этикеток ──────────────────────────────────────────────────────────

export const LABEL_SIZES = [
  { id: "58x40",  label: "58 × 40 мм",  width: "58mm",  height: "40mm"  },
  { id: "58x30",  label: "58 × 30 мм",  width: "58mm",  height: "30mm"  },
  { id: "40x25",  label: "40 × 25 мм",  width: "40mm",  height: "25mm"  },
  { id: "100x50", label: "100 × 50 мм", width: "100mm", height: "50mm"  },
  { id: "120x75", label: "120 × 75 мм", width: "120mm", height: "75mm"  },
  { id: "15x15",  label: "15 × 15 мм",  width: "15mm",  height: "15mm"  },
  { id: "20x20",  label: "20 × 20 мм",  width: "20mm",  height: "20mm"  },
  { id: "25x25",  label: "25 × 25 мм",  width: "25mm",  height: "25mm"  },
  { id: "20x30",  label: "20 × 30 мм",  width: "20mm",  height: "30mm"  },
  { id: "30x30",  label: "30 × 30 мм",  width: "30mm",  height: "30mm"  },
  { id: "40x40",  label: "40 × 40 мм",  width: "40mm",  height: "40mm"  },
  { id: "20x40",  label: "20 × 40 мм",  width: "20mm",  height: "40mm"  },
  { id: "30x40",  label: "30 × 40 мм",  width: "30mm",  height: "40mm"  },
  { id: "30x58",  label: "30 × 58 мм",  width: "30mm",  height: "58mm"  },
];

// ─── GS1 Data Matrix encoder (ECC200, FNC1) ─────────────────────────────────────

function drawBitMatrixToCanvas(canvas, matrix, pixelSize) {
  const sourceW = matrix.getWidth();
  const sourceH = matrix.getHeight();
  const quietModules = 2;
  const modulePx = Math.max(1, Math.floor(pixelSize / (Math.max(sourceW, sourceH) + quietModules * 2)));
  const drawW = (sourceW + quietModules * 2) * modulePx;
  const drawH = (sourceH + quietModules * 2) * modulePx;
  const offsetX = Math.floor((pixelSize - drawW) / 2);
  const offsetY = Math.floor((pixelSize - drawH) / 2);
  const ctx = canvas.getContext("2d");

  canvas.width = pixelSize;
  canvas.height = pixelSize;
  ctx.imageSmoothingEnabled = false;
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, pixelSize, pixelSize);
  ctx.fillStyle = "#000";

  for (let y = 0; y < sourceH; y += 1) {
    for (let x = 0; x < sourceW; x += 1) {
      if (matrix.get(x, y)) {
        ctx.fillRect(offsetX + (x + quietModules) * modulePx, offsetY + (y + quietModules) * modulePx, modulePx, modulePx);
      }
    }
  }
}

function randomizeDataMatrixPad(position) {
  const pseudoRandom = ((149 * position) % 253) + 1;
  const temp = DATA_MATRIX_PAD + pseudoRandom;
  return temp <= 254 ? temp : temp - 254;
}

function encodeGs1DataMatrixAscii(value) {
  let codewords = String.fromCharCode(232);

  for (let i = 0; i < value.length; i += 1) {
    const charCode = value.charCodeAt(i);
    if (i + 1 < value.length && /\d/.test(value[i]) && /\d/.test(value[i + 1])) {
      codewords += String.fromCharCode(130 + Number(value.slice(i, i + 2)));
      i += 1;
      continue;
    }
    if (charCode <= 127) {
      codewords += String.fromCharCode(charCode + 1);
      continue;
    }
    if (charCode <= 255) {
      codewords += String.fromCharCode(235, charCode - 127);
      continue;
    }
    throw new Error("Only ASCII and extended ASCII are supported for GS1 Data Matrix");
  }

  const symbolInfo = DataMatrixSymbolInfo.lookup(codewords.length, FORCE_SQUARE, null, null, true);
  const capacity = symbolInfo.getDataCapacity();
  if (codewords.length < capacity) {
    codewords += String.fromCharCode(DATA_MATRIX_PAD);
  }
  while (codewords.length < capacity) {
    codewords += String.fromCharCode(randomizeDataMatrixPad(codewords.length + 1));
  }

  return { codewords, symbolInfo };
}

export function renderGs1DataMatrix(canvas, rawValue, pixelSize) {
  const value = normalizeCodeValue(rawValue);
  const { codewords, symbolInfo } = encodeGs1DataMatrixAscii(value);
  const ecc = DataMatrixErrorCorrection.encodeECC200(codewords, symbolInfo);
  const placement = new DataMatrixDefaultPlacement(ecc, symbolInfo.getSymbolDataWidth(), symbolInfo.getSymbolDataHeight());
  placement.place();
  const matrix = new DataMatrixWriter().encodeLowLevel(placement, symbolInfo, 0, 0);
  drawBitMatrixToCanvas(canvas, matrix, pixelSize);
}

export function dataMatrixDataUrl(rawValue, pixelSize) {
  const canvas = document.createElement("canvas");
  renderGs1DataMatrix(canvas, rawValue, pixelSize);
  return canvas.toDataURL("image/png");
}

// ─── Code 128 / 1D barcode → data URL ─────────────────────────────────────────

export function barcodeDataUrl(value, { format = "CODE128", height = 60, displayValue = true, fontSize = 14 } = {}) {
  const canvas = document.createElement("canvas");
  JsBarcode(canvas, value, {
    format,
    lineColor: "#000",
    background: "#fff",
    width: 2,
    height,
    displayValue,
    fontSize,
    margin: 4,
  });
  return canvas.toDataURL("image/png");
}

// Как barcodeDataUrl, но дополнительно возвращает натуральные пиксельные
// размеры холста — нужны, чтобы полоса штрихкода на этикетке была задана по
// РЕАЛЬНЫМ пропорциям картинки, а не выдумана заранее (см. czGridParams).
function barcodeCanvasMeta(value, { format = "CODE128", height = 50, fontSize = 20, moduleWidth = 3 } = {}) {
  const canvas = document.createElement("canvas");
  JsBarcode(canvas, value, {
    format,
    lineColor: "#000",
    background: "#fff",
    width: moduleWidth,
    height,
    displayValue: true,
    fontSize,
    margin: 4,
  });
  return { dataUrl: canvas.toDataURL("image/png"), width: canvas.width, height: canvas.height };
}

// ─── компоновка этикетки ЧЗ (Data Matrix + текст + доп. штрихкод) ──────────────
// Общая для «Печати этикетки» и «Ленты заказов» — код в одном месте.
//
// Штрихкод — отдельная горизонтальная полоса на 90% ширины этикетки, снизу
// (а не втиснут в текстовую колонку — там он был нечитаемо мелким). Когда
// рядом есть текст (название/даты), ЧЗ ограничен 50% ширины — иначе он
// перетягивает место и текст между ним и штрихкодом становится нечитаемым.

const PX_PER_MM      = 3.78; // 96dpi
const CODE_GRID_FR   = 2;
const TEXT_GRID_FR   = 1;
const GRID_TOTAL_FR  = CODE_GRID_FR + TEXT_GRID_FR;
const BARCODE_STRIP_WIDTH_FRAC  = 0.9;  // доля рабочей ширины этикетки — фиксирована
const BARCODE_STRIP_MAX_H_MM    = 18;   // потолок высоты полосы (не даём ей «съесть» всю этикетку)
const BARCODE_STRIP_MIN_H_MM    = 4;
const BARCODE_STRIP_GAP_MM      = 1.2;

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function textLineCount(hasTitle, dateFields, dateValues) {
  let count = hasTitle ? 1 : 0;
  if (dateFields?.mfg && dateValues?.mfg) count += 2;
  if (dateFields?.shelf && dateValues?.shelf?.value) count += 2;
  if (dateFields?.exp && dateValues?.exp) count += 2;
  return Math.max(1, count);
}

function fontSizeFromBox(widthMm, heightMm, lines) {
  const byHeight = (heightMm / Math.max(1, lines)) * 2.15;
  const byWidth = widthMm * 1.8;
  return clamp(Math.min(byHeight, byWidth), 3.2, 9);
}

// hasSideText — есть ли название/даты (определяет раскладку код+текст в
// главной области). barcodeConverted/barcodeFormat — если заданы, штрихкод
// рендерится ЗДЕСЬ (один раз) и его РЕАЛЬНЫЕ пропорции определяют высоту
// полосы при фиксированной ширине 90% — иначе object-fit: contain вписывает
// картинку по высоте бокса и сжимает её ширину до 10-15% вместо 90%.
function czGridParams(widthMm, heightMm, hasSideText, barcodeConverted, barcodeFormat, lineCount = 1) {
  const w = parseFloat(widthMm);
  const h = parseFloat(heightMm);
  const workW = Math.max(1, w - LABEL_INSET_MM * 2);
  const workH = Math.max(1, h - LABEL_INSET_MM * 2);
  const horiz = w >= h;

  let barcodeStripW = 0, barcodeStripH = 0, barcodeDataUrlOut = "", barcodeError = "";
  if (barcodeConverted) {
    try {
      const meta = barcodeCanvasMeta(barcodeConverted, { format: barcodeFormat });
      barcodeStripW = workW * BARCODE_STRIP_WIDTH_FRAC;
      const aspect = meta.height / meta.width; // натуральное отношение высоты к ширине
      barcodeStripH = clamp(barcodeStripW * aspect, BARCODE_STRIP_MIN_H_MM, BARCODE_STRIP_MAX_H_MM);
      barcodeDataUrlOut = meta.dataUrl;
    } catch {
      barcodeError = `Некорректное значение для формата ${barcodeFormat}`;
    }
  }
  const hasBarcodeStrip = barcodeStripH > 0;
  const mainH = Math.max(1, workH - (hasBarcodeStrip ? barcodeStripH + BARCODE_STRIP_GAP_MM : 0));

  // Со штрихкодом рядом ЧЗ не должен доминировать — фиксируем 50% вместо 2/3.
  const codeFraction = hasBarcodeStrip ? 0.5 : CODE_GRID_FR / GRID_TOTAL_FR;
  const textFraction = 1 - codeFraction;

  const codeW = hasSideText && horiz ? workW * codeFraction : workW;
  const codeH = hasSideText && !horiz ? mainH * codeFraction : mainH;
  const textW = hasSideText ? (horiz ? workW * textFraction : workW) : 0;
  const textH = hasSideText ? (horiz ? mainH : mainH * textFraction) : 0;
  const codeSizeMm = Math.max(1, Math.min(codeW, codeH));
  const fs = fontSizeFromBox(textW || workW, textH || mainH, lineCount);

  return {
    workW, workH, mainH, codeW, codeH, textW, textH,
    fs,
    titleFs: clamp(fs * 1.08, 3.6, 10),
    qrMm: codeSizeMm,
    qrPx: Math.max(200, Math.round(codeSizeMm * PX_PER_MM * 4)),
    barcodeStripH, barcodeStripW, barcodeDataUrl: barcodeDataUrlOut, barcodeError,
    horiz,
    noText: !hasSideText,
  };
}

const czFmtDate = (s) => (s || "").split("-").reverse().join(".");
const czShelfLabel = (value, unit) =>
  value ? `${value} ${{ days: "дн.", months: "мес.", years: "лет" }[unit] || unit}` : "";

// Метаданные раскладки для превью в браузере (без построения HTML) — уже
// содержит готовый barcodeDataUrl для картинки превью, генерировать отдельно не нужно.
export function czLabelPreviewMeta({ title = "", dateFields = {}, dateValues = {}, barcodeConverted = "", barcodeFormat = "CODE128", labelW, labelH }) {
  const hasTitle = !!title.trim();
  const hasText = !!(hasTitle || dateFields.mfg || dateFields.shelf || dateFields.exp);
  const textLines = textLineCount(hasTitle, dateFields, dateValues);
  const layout = czGridParams(labelW, labelH, hasText, barcodeConverted, barcodeFormat, textLines);
  return { hasText, showBarcode: !!layout.barcodeDataUrl, barcodeError: layout.barcodeError, layout };
}

// Полный HTML документ печати одной или нескольких копий этикетки ЧЗ.
export function buildCzLabelHtml({
  value, title = "", dateFields = {}, dateValues = {}, barcodeConverted = "", barcodeFormat = "CODE128",
  font = "Arial", labelW, labelH, copies = 1,
}) {
  const hasTitle = !!title.trim();
  const hasText = !!(hasTitle || dateFields.mfg || dateFields.shelf || dateFields.exp);
  const textLines = textLineCount(hasTitle, dateFields, dateValues);
  const p = czGridParams(labelW, labelH, hasText, barcodeConverted, barcodeFormat, textLines);
  const matrixImageUrl = dataMatrixDataUrl(value, Math.max(600, p.qrPx));
  const barcodeImgUrl = p.barcodeDataUrl;

  const labelItems = Array.from({ length: copies }, () => {
    const barcodeSlot =
      `<div class="qr-square" style="width:${p.qrMm}mm;height:${p.qrMm}mm;">
         <img class="matrix-img" src="${matrixImageUrl}" alt="" />
       </div>`;

    const mainHtml = p.noText
      ? `<div class="main main--center">${barcodeSlot}</div>`
      : (() => {
          const titleHtml = hasTitle ? `<div class="lbl-title">${title}</div>` : "";
          const dateRow = (label, val) => `<div class="lbl-date"><span class="dk">${label}</span><b>${val}</b></div>`;
          const datesHtml = [
            dateFields.mfg && dateValues.mfg ? dateRow("Дата производства", czFmtDate(dateValues.mfg)) : "",
            dateFields.shelf && dateValues.shelf?.value ? dateRow("Срок годности", czShelfLabel(dateValues.shelf.value, dateValues.shelf.unit)) : "",
            dateFields.exp && dateValues.exp ? dateRow("Годен до", czFmtDate(dateValues.exp)) : "",
          ].filter(Boolean).join("");
          const cls = p.horiz ? "main--horiz" : "main--portrait";
          return `<div class="main ${cls}"><div class="code-cell code-cell--fixed">${barcodeSlot}</div><div class="text-block">${titleHtml}${datesHtml}</div></div>`;
        })();

    const barcodeStripHtml = barcodeImgUrl
      ? `<div class="lbl-barcode" style="width:${p.barcodeStripW}mm;height:${p.barcodeStripH}mm;"><img class="barcode-img" src="${barcodeImgUrl}" alt="" /></div>`
      : "";

    return `<div class="label">${mainHtml}${barcodeStripHtml}</div>`;
  });

  const pages = labelItems.join("<div class='pb'></div>");

  return `<!DOCTYPE html><html><head>
    <meta charset="utf-8"/>
    <style>
      @page { size: ${labelW} ${labelH}; margin: 0; }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: "${font}", sans-serif; width: ${labelW}; }
      .pb { page-break-after: always; }

      .label {
        width: ${labelW}; height: ${labelH};
        padding: ${LABEL_INSET_MM}mm;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        align-items: center;
      }

      .main {
        width: 100%;
        height: ${p.mainH}mm;
        display: grid;
        overflow: hidden;
      }
      .main--center { place-items: center; }
      .main--horiz {
        grid-template-columns: ${p.codeW}mm ${p.textW}mm;
        grid-template-rows: 1fr;
      }
      .main--portrait {
        grid-template-columns: 1fr;
        grid-template-rows: ${p.codeH}mm ${p.textH}mm;
      }

      .code-cell {
        min-width: 0; min-height: 0;
        display: flex; align-items: center; justify-content: center;
        overflow: hidden;
      }
      .qr-square { flex: 0 0 auto; display: block; overflow: hidden; }
      .matrix-img {
        display: block; width: 100%; height: 100%;
        object-fit: contain; image-rendering: pixelated;
        print-color-adjust: exact; -webkit-print-color-adjust: exact;
      }

      .text-block {
        min-width: 0; min-height: 0;
        display: flex; flex-direction: column;
        align-items: flex-start; justify-content: center;
        overflow: hidden; line-height: 1.08; gap: 0.6mm;
      }
      .main--portrait .text-block { align-items: center; text-align: center; }

      .lbl-title {
        font-size: ${p.titleFs}pt; font-weight: bold;
        max-width: 100%; overflow-wrap: anywhere; margin-bottom: 0.2mm;
      }
      .lbl-date {
        font-size: ${p.fs}pt; max-width: 100%;
        display: flex; flex-direction: column; overflow-wrap: anywhere;
      }
      .lbl-date b { font-size: ${p.fs}pt; }
      .dk { color: #666; }

      .lbl-barcode {
        margin-top: ${BARCODE_STRIP_GAP_MM}mm;
        display: flex; align-items: center; justify-content: center; overflow: hidden;
      }
      .barcode-img {
        display: block; width: 100%; height: 100%;
        object-fit: contain;
        print-color-adjust: exact; -webkit-print-color-adjust: exact;
      }
    </style>
  </head><body>${pages}</body></html>`;
}

// ─── общий помощник печати ─────────────────────────────────────────────────────
// labels — массив строк-HTML содержимого этикеток; каждая печатается на своей
// странице заданного физического размера.

export function buildPrintDocument({ widthMm, heightMm, font = "Arial", labels, extraCss = "" }) {
  const pages = labels
    .map((it) => {
      const inner = typeof it === "string" ? it : it.inner;
      const cls = typeof it === "string" ? "" : (it.cls || "");
      return `<div class="cz-lbl${cls ? ` ${cls}` : ""}">${inner}</div>`;
    })
    .join("<div class='cz-pb'></div>");
  return `<!DOCTYPE html><html><head>
    <meta charset="utf-8"/>
    <style>
      @page { size: ${widthMm} ${heightMm}; margin: 0; }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: "${font}", sans-serif; }
      .cz-pb { page-break-after: always; }
      .cz-lbl {
        width: ${widthMm}; height: ${heightMm};
        padding: ${LABEL_INSET_MM}mm;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }
      .cz-lbl img { display: block; image-rendering: pixelated;
        print-color-adjust: exact; -webkit-print-color-adjust: exact; }
      .cz-title { font-weight: bold; text-align: center; overflow-wrap: anywhere; width: 100%; }
      .cz-sub { text-align: center; overflow-wrap: anywhere; width: 100%; color: #222; }
      ${extraCss}
    </style>
  </head><body>${pages}</body></html>`;
}

export function openPrintWindow(html, { title } = {}) {
  const win = window.open("", "_blank", "width=520,height=420");
  if (!win) return false;
  if (title) win.document.title = title;
  win.document.write(html);
  win.document.close();
  win.onload = () => setTimeout(() => { win.print(); win.close(); }, 200);
  return true;
}

// ─── парсинг вставленного списка КИЗ ───────────────────────────────────────────
// Принимает многострочный текст (в т.ч. с табличными колонками и мусором),
// достаёт из каждой строки код маркировки и нормализует его.

export function parseKizList(text) {
  if (!text) return [];
  const out = [];
  const lines = text.split(/\r\n|\r|\n/);
  for (const lineRaw of lines) {
    const line = lineRaw.trim();
    if (!line) continue;

    // Табличная строка (name<TAB>type<TAB>codes): берём ячейку с кодами.
    let cell = line;
    if (/\t|;/.test(line)) {
      const cells = line.split(/\t|;/).map((c) => c.trim()).filter(Boolean);
      cell = cells.find((c) => /01\d{6,}/.test(c)) || cells[cells.length - 1] || line;
    }

    // Несколько кодов в одной строке разделяются запятой + пробелом («, »).
    // Внутри серийного номера запятая идёт без пробела (напр. «QV,78»),
    // поэтому режем только по «,\s+», не трогая запятые внутри кода.
    const tokens = cell.split(/,[ \t]+/);
    for (const tokRaw of tokens) {
      const tok = tokRaw.trim();
      if (!tok) continue;
      const code = normalizeCodeValue(tok);
      // код маркировки должен содержать 01 + не менее 6 цифр (GTIN)
      if (code && /01\d{6,}/.test(code)) out.push(code);
    }
  }
  return out;
}

// Разбор одного КИЗ на составляющие AI (для отображения / валидации)
export function parseKizParts(code) {
  const segs = (code || "").split(GS);
  const first = segs[0] || "";
  const gtinMatch = first.match(/01(\d{14})/);
  const gtin = gtinMatch ? gtinMatch[1] : "";
  let serial = "";
  if (gtinMatch) {
    const after = first.slice(first.indexOf(gtinMatch[0]) + gtinMatch[0].length);
    const m = after.match(/^21(\S+)/);
    serial = m ? m[1] : "";
  }
  const rest = segs.slice(1);
  const grp = (ai) => {
    const s = rest.find((x) => x.startsWith(ai));
    return s ? s.slice(ai.length) : "";
  };
  return {
    gtin, serial,
    key91: grp("91"),
    code92: grp("92"),
    code93: grp("93"),
    valid: !!gtin && !!serial,
  };
}
